import { useRef } from 'react';
import type React from 'react';

const SWIPE_THRESHOLD = 50;

export function useSwipeNavigation(
  onNext: () => void,
  onBack: () => void,
  disabled = false,
): { onTouchStart: React.TouchEventHandler; onTouchEnd: React.TouchEventHandler } {
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);

  const onTouchStart: React.TouchEventHandler = e => {
    // Don't arm a swipe while disabled, or for multi-touch gestures (e.g. pinch-zoom).
    // Capturing a start point now would let the gesture navigate if `disabled` flips
    // to false before the finger lifts (e.g. an async action settles mid-swipe).
    if (disabled || e.touches.length !== 1) {
      startX.current = null;
      startY.current = null;
      return;
    }
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
  };

  const onTouchEnd: React.TouchEventHandler = e => {
    const downX = startX.current;
    const downY = startY.current;
    // Consume the gesture exactly once: clear the start point up front so no
    // early return below can leave a stale origin for the next touch.
    startX.current = null;
    startY.current = null;

    if (disabled || downX === null || downY === null || e.changedTouches.length !== 1) return;

    const deltaX = e.changedTouches[0].clientX - downX;
    const deltaY = e.changedTouches[0].clientY - downY;

    // Require a horizontal-dominant swipe past the threshold; ignore vertical scrolls.
    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    if (deltaX < 0) {
      onNext();
    } else {
      onBack();
    }
  };

  return { onTouchStart, onTouchEnd };
}
